'use strict';

import React, { useMemo, useState, useCallback, useRef } from 'react'

import PageWrapper from '@Layout/PageWrapper'
import Layout      from '@Layout/BackOffice'

import Dashboard from '@/Lists/Dashboard'

import CreditNoteRow from '@/Lists/Rows/CreditNoteRow'

import FormElement from '@/Form/toolkit/FormElement'
import DatePicker  from '@/Form/V2/DatePicker'

import SearchBar from '@Pure/SearchBar'
import Link      from '@Pure/Link'

import getUrlParameter from '@function/getUrlParameter'

const DASHBOARD = 'CreditNotesList';

const sortable_columns =
[
	'createdAt'
];

const header =
[
	{
		label: 'Date', name: 'date'
	},
	{
		label: 'Au nom de', name: 'name'
	},
	{
		label: 'Commande', name: 'order_number'
	},
	{
		label: 'Facture', name: 'bill_number'
	},
	{
		label: 'N° Avoir', name: 'number'
	},
	{
		label: 'Montant', name: 'amount'
	},
	{
		label: 'Règlement', name: 'payment_status'
	},
	{
		label: 'Solde', name: 'remaining_unpaid'
	},
	{
		label: '', name: 'PDF'
	}
]
.map(opt => ({ ...opt, sortable: sortable_columns.includes(opt.name) }));

const fields = header.map(data => data.name);

const fields_with_timeout =
[
	'searched', 'searched_number', 'date_from', 'date_to', 'amount'
];

const status_messages = { };

const storage_filters =
{
	searched: window.localStorage.getItem(DASHBOARD + '.searched') || '',
	searched_number: window.localStorage.getItem(DASHBOARD + '.searched_number') || '',
	amount: window.localStorage.getItem(DASHBOARD + '.amount') || ''
};

export default PageWrapper(function PaymentsDashboardPage (props)
{
	const search_timeout = useRef();

	const [instant_filters, setInstantFilters] = useState(
	{
		...storage_filters,

		draft_only: window.localStorage.getItem(DASHBOARD + '.draft_only') || '',
		id: getUrlParameter('id', window.location.href)
	});

	const [delayed_filters, setDelayedFilters] = useState({ ...storage_filters });

	const handleChange = useCallback(function ({ value, e, name })
	{
		if (e === null && fields_with_timeout.includes(name) === false) return;

	// Prevent on mount trigger
		if (e === null && fields_with_timeout.includes(name)) return;

		window.localStorage.setItem(DASHBOARD + '.' + name, value);

		setInstantFilters(function (filters)
		{
			return { ...filters, [name]: value };
		});

		if (fields_with_timeout.includes(name) === false) return;

		if (search_timeout.current)
		{
			window.clearTimeout(search_timeout.current);
		}

		search_timeout.current = window.setTimeout(function ()
		{
			search_timeout.current = undefined;

			setDelayedFilters(function (delayed_filters)
			{
				return { ...delayed_filters, [name]: value };
			});
		},
		320);
	},
	[]);

	const handleFormElChange = useCallback(function (e, data)
	{
		handleChange({ e, ...data });
	},
	[handleChange]);

	const handleSearchChange = useCallback(function (value, data={ }, e)
	{
		handleChange({ value: value, name: data?.name, e });
	},
	[handleChange]);

	const filters = useMemo(function ()
	{
		const filters =
		{
			searched: delayed_filters.searched,
			searched_number: delayed_filters.searched_number,
			draft_only: instant_filters.draft_only,
			date_from: delayed_filters.date_from,
			date_to: delayed_filters.date_to,
			amount: delayed_filters.amount
		};

		if (instant_filters.id) filters.id = instant_filters.id;

		return filters;
	},
	[instant_filters, delayed_filters]);

	return (
		<Layout page_title="Avoirs" history={ props.history }>

			<div className="page-content">

				<div className="o-grid-4 u-flex-wrap u-content-between u-items-end
					u-margin-bottom-15">

					<div className="o-grid__item u-flex u-items-end u-gap-10">
						<DatePicker name="date_from" label="Du"
							onChange={ handleChange }
							value={ instant_filters.date_from }
							bemit={ ['50%'] }
						/>

						<DatePicker name="date_to" label="Au"
							onChange={ handleChange }
							value={ instant_filters.date_to }
							bemit={ ['50%'] }
						/>
					</div>

					<div className="o-grid__item">
						<SearchBar hide_btn={ true }
							placeholder="Avoir à (CSE ou Membre)"
							handleChange={ handleSearchChange }
							value={ instant_filters.searched }
							name="searched"
							className="c-searchbar c-searchbar--dashboard"
						/>
					</div>

					<div className="o-grid__item">
						<SearchBar hide_btn={ true }
							placeholder="Rechercher par identifiant"
							handleChange={ handleSearchChange }
							value={ instant_filters.searched_number }
							name="searched_number"
							className="c-searchbar c-searchbar--dashboard"
						/>
					</div>

					<div className="o-grid__item">
						<SearchBar hide_btn={ true }
							placeholder="Par montant (ex 40.5)"
							handleChange={ handleSearchChange }
							value={ instant_filters.amount }
							name="amount"
							className="c-searchbar c-searchbar--dashboard"
						/>
					</div>

					<div className="o-grid__item u-flex u-items-center">
						<FormElement name="draft_only"
							type="checkbox" data={ instant_filters }
							onChange={ handleFormElChange }
							status={ status_messages }
							label="Surplus d'un règlement"
							className="u-margin-top-0"
						/>
					</div>
				</div>

				<div className="o-content">

					<div className="u-flex u-content-end u-margin-bottom-20">
						<Link href="/gestion-des-avoirs/nouveau"
							className="c-btn c-btn--add u-no-margin-top">
							Nouvel avoir
						</Link>
					</div>

				</div>

				<div className="o-content">
					<Dashboard model="CreditNote" items-per-page={ 50 }
						url="/dashboard/credit-notes" filters={ filters }
						header={ header } fields={ fields }
						order="DESC" order-by="date"
						request-update="UpdateCreditNotesList"
						disable-sort={ filters.searched || filters.searched_number
							? true : false }
						component={ CreditNoteRow }
					/>
				</div>
			</div>

		</Layout>
	);
});